// Onboarding-call booking links (free-platform pivot, Aug 2026).
//
// Every "book your call" surface in the dashboard routes through here so the
// event, the UTMs and — critically — the brand id stay consistent. The brand
// id rides in utm_content, which Calendly echoes back on the invitee.created
// webhook; worker/src/calendly_webhook.ts reads it to stamp
// brand_configs.launch_call_booked_at against the right brand. Hand-rolling a
// Calendly URL anywhere else silently breaks that stamp, and the brand gets
// nagged to book a call they already booked.
//
// Search for CALENDLY_EVENT when swapping in a different event type.

const CALENDLY_EVENT = 'https://calendly.com/d/d2gd-3vh-tmm/creator-roster'

/**
 * Marks a utm_content as an AGENCY account id rather than a brand id.
 * worker/src/calendly_webhook.ts splits on this; keep the two in step.
 */
export const ACCOUNT_UTM_PREFIX = 'account:'

/**
 * Max width for an inline embed, in px.
 *
 * Calendly drops to its cramped single-column layout somewhere around 680px,
 * so this has to clear that — but letting the embed fill a wide container (the
 * onboarding shell is 960px inside its padding) leaves it looking cavernous.
 * 760 clears the breakpoint with margin to spare and still reads as a dialog
 * rather than a page. Shared so the onboarding and post-publish embeds can't
 * drift apart.
 */
export const CALENDLY_EMBED_MAX_WIDTH = 760

/** Which surface the booking came from — lands in utm_medium for attribution. */
export type CalendlyMedium =
  | 'onboarding'        // step 3 of signup (inline embed)
  | 'agency_checklist'  // agency lobby Get-Started checklist
  | 'post_publish'      // LaunchCall interstitial after the first job goes live
  | 'checklist'         // Home Get-Started checklist
  | 'listings_banner'   // Listings page call banner
  | 'sidebar_managed'   // sidebar plan badge — managed-service upsell
  | 'how_it_works'      // /how-it-works CTA
  // Managed-services pitch placements (dev call 2026-09-04). Kept SEPARATE from
  // the onboarding-era mediums above rather than renaming them, so Calendly
  // analytics can tell a qualified managed-intent click from a brand who was
  // pushed into an onboarding call under the old flow.
  | 'managed_home'          // Home ManagedServicesBanner
  | 'managed_post_publish'  // post-publish interstitial
  | 'managed_listings'      // Listings banner
  | 'managed_how_it_works'  // /how-it-works CTA
  | 'managed_agency'        // agency lobby ManagedServicesBanner

export interface CalendlyLinkOptions {
  /** Stamped into utm_content so the webhook can resolve the brand. */
  brandId?: string | null
  /**
   * An AGENCY account booking. Takes precedence over brandId: under an agency
   * the call belongs to the account, and its brands show no booking prompts at
   * all, so the two are never both meaningful on one link.
   */
  accountId?: string | null
  /** Prefills Calendly's email field. */
  email?: string | null
  /** Prefills Calendly's name field. */
  name?: string | null
}

function baseParams(medium: CalendlyMedium, opts: CalendlyLinkOptions): URLSearchParams {
  const p = new URLSearchParams()
  p.set('utm_source', 'platform')
  p.set('utm_medium', medium)
  // utm_content identifies who booked — see the webhook note above. Omitted
  // during onboarding, where neither row exists yet; /api/onboard (brands) and
  // /api/agency/init (agencies) read booked_calls back by email instead.
  //
  // A BARE uuid means a brand. That is deliberately unchanged: links minted
  // before agencies existed are still live in inboxes and calendar invites, and
  // re-interpreting them would silently stop stamping. An agency is tagged with
  // the ACCOUNT_UTM_PREFIX instead, which no legacy link can collide with.
  if (opts.accountId) p.set('utm_content', `${ACCOUNT_UTM_PREFIX}${opts.accountId}`)
  else if (opts.brandId) p.set('utm_content', opts.brandId)
  if (opts.email) p.set('email', opts.email)
  if (opts.name) p.set('name', opts.name)
  return p
}

/** Plain booking link — for anchors that open Calendly in a new tab. */
export function calendlyUrl(medium: CalendlyMedium, opts: CalendlyLinkOptions = {}): string {
  return `${CALENDLY_EVENT}?${baseParams(medium, opts).toString()}`
}

/**
 * Inline-embed URL. Adds the embed handshake params (embed_domain drives the
 * postMessage origin) plus the dark theming the two in-app embeds use.
 */
export function calendlyEmbedUrl(medium: CalendlyMedium, opts: CalendlyLinkOptions = {}): string {
  const p = baseParams(medium, opts)
  p.set('hide_gdpr_banner', '1')
  p.set('hide_event_type_details', '1')
  p.set('primary_color', 'fb3aae')
  p.set('background_color', '141414')
  p.set('text_color', 'ffffff')
  p.set('embed_domain', window.location.hostname)
  p.set('embed_type', 'Inline')
  return `${CALENDLY_EVENT}?${p.toString()}`
}

/**
 * Guard for Calendly's iframe postMessage traffic. Calendly posts from
 * https://calendly.com; anything else on the page (ad scripts, extensions, a
 * hostile embed) must not be able to fake `calendly.event_scheduled` and mark
 * a call as booked. Exact-origin match, not a substring test — "calendly.com"
 * appears in evil-calendly.com.attacker.test too.
 */
export function isCalendlyOrigin(origin: unknown): boolean {
  return typeof origin === 'string' && /^https:\/\/([a-z0-9-]+\.)*calendly\.com$/.test(origin)
}

/** Shape of the messages Calendly's embed broadcasts to the host page. */
export interface CalendlyMessage {
  event?: string
  payload?: { height?: string | number }
}

/** Clamp for the content-height Calendly reports, so the iframe can't be resized absurdly. */
export function clampCalendlyHeight(raw: string | number | undefined, fallback: number): number {
  if (raw == null) return fallback
  const h = parseInt(String(raw), 10)
  if (isNaN(h) || h <= 0) return fallback
  return Math.min(Math.max(h, 400), 1400)
}
